import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Moon, Sun, User, LogOut, ChevronDown, BookOpen, PlayCircle, LayoutDashboard } from "lucide-react";
import { Button } from "../../components/elements/Button";
import { motion, AnimatePresence } from "framer-motion";
import { framerTransition } from "../../motion/motionTokens.ts";
import { useTheme } from "../../../../app/providers/ThemeContext";
import { useAuth } from "../../../../app/providers/AuthContext";
import meeLogo from "../../../assets/img/mee-logo.png";
import { useRecentActivity } from "../../../../modules/profile/presentation/hooks/useRecentActivity";

const FamilyBusinessNavbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const { user, isAuthenticated, logout } = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { getLearningLink } = useRecentActivity();
    const learningLink = getLearningLink();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleLogout = () => {
        logout();
        setIsDropdownOpen(false);
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-999 transition-shell ${isMobileMenuOpen
                ? "bg-white dark:bg-[#0F172A] shadow-sm py-3"
                : isScrolled
                    ? "bg-white/90 dark:bg-[#0F172A]/90 backdrop-blur-md shadow-sm py-3"
                    : "bg-transparent py-5"
                }`}
        >
            <div className="mx-auto w-full max-w-[min(100%,96rem)] px-4">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/family-business" className="flex items-center gap-3 group">
                        <img src={meeLogo} alt="Mulia Executive Education" className="h-10 w-auto group-hover:scale-105 transition-transform-micro" />
                        <div className="flex flex-col">
                            <span className="text-lg font-bold text-gray-900 dark:text-white leading-none group-hover:text-amber-600 transition-theme-micro">
                                Mulia Executive
                            </span>
                            <span className="text-[10px] font-semibold text-amber-600 tracking-wider uppercase">
                                Education
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Nav - Removed as per request */}
                    <nav className="hidden md:flex items-center gap-8">
                    </nav>

                    {/* CTA & Mobile Toggle */}
                    <div className="flex items-center gap-4">
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 transition-theme-micro"
                            aria-label="Toggle theme"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>

                        {/* User Dropdown or Login Button */}
                        {isAuthenticated && user ? (
                            <div className="relative hidden md:block">
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="flex items-center gap-3 p-1 pr-3 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-theme"
                                >
                                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-sm">
                                        {user.name.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-25 truncate">
                                        {user.name.split(' ')[0]}
                                    </span>
                                    <motion.div
                                        animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                                        transition={framerTransition("micro")}
                                    >
                                        <ChevronDown size={14} className="text-gray-500" />
                                    </motion.div>
                                </button>

                                <AnimatePresence>
                                    {isDropdownOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={framerTransition("micro")}
                                            className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-[#0F172A]/90 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-theme"
                                        >
                                            <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                                <p className="font-bold text-gray-900 dark:text-white truncate">{user.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                                            </div>
                                            <div className="p-2">
                                                {[
                                                    { icon: <User size={16} />, label: "Profile", href: "/profile" },
                                                    { icon: <BookOpen size={16} />, label: "Kelas Saya", href: "/my-courses" },
                                                    { icon: <PlayCircle size={16} />, label: "Lanjut Belajar", href: learningLink },
                                                    // Admin/Instructor Menu
                                                    ...((user.role === 'admin' || user.role === 'instructor') ? [
                                                        { icon: <LayoutDashboard size={16} />, label: "Admin Dashboard", href: "/admin" },
                                                    ] : [])
                                                ].map((item, idx) => (
                                                    <Link
                                                        key={idx}
                                                        to={item.href}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-theme-micro group"
                                                        onClick={() => setIsDropdownOpen(false)}
                                                    >
                                                        <span className="text-gray-400 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-theme-micro">{item.icon}</span>
                                                        {item.label}
                                                    </Link>
                                                ))}
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-theme-micro mt-1"
                                                >
                                                    <LogOut size={16} />
                                                    Logout
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <Link to="/login">
                                <Button className="hidden md:flex rounded-xl px-6" variant="family">
                                    Mulai Sekarang
                                </Button>
                            </Link>
                        )}

                        {/* Mobile Menu Toggle */}
                        <button
                            className="md:hidden p-2 text-gray-600 dark:text-gray-300"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? <X /> : <Menu />}
                        </button>
                    </div>
                </div >
            </div >

            {/* Mobile Menu */}
            <AnimatePresence>
                {
                    isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={framerTransition("surface")}
                            className="md:hidden bg-white dark:bg-[#0F172A] rounded-b-2xl transition-theme"
                        >
                            <nav className="mx-auto w-full max-w-[min(100%,96rem)] px-4 py-4 flex flex-col">
                                {isAuthenticated && user && (
                                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl mb-2">
                                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900 dark:text-white text-sm">{user.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-50">{user.email}</p>
                                        </div>
                                    </div>
                                )}

                                {isAuthenticated ? (
                                    <>
                                        <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
                                        <Link to="/profile" className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 py-2 transition-theme-micro" onClick={() => setIsMobileMenuOpen(false)}>Profile</Link>
                                        <Link to="/my-courses" className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 py-2 transition-theme-micro" onClick={() => setIsMobileMenuOpen(false)}>Kelas Saya</Link>
                                        <Link to={learningLink} className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 py-2 transition-theme-micro" onClick={() => setIsMobileMenuOpen(false)}>Lanjut Belajar</Link>

                                        {/* Admin Dashboard for Mobile */}
                                        {(user?.role === 'admin' || user?.role === 'instructor') && (
                                            <>
                                                <Link
                                                    to="/admin"
                                                    className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 py-2 transition-theme-micro"
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    Admin Dashboard
                                                </Link>

                                            </>
                                        )}
                                        <button onClick={handleLogout} className="text-base font-medium text-red-600 hover:text-red-700 py-2 text-left transition-theme-micro">Logout</button>
                                    </>
                                ) : (
                                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                                        <Button className="w-full justify-center rounded-xl bg-amber-600 hover:bg-amber-700 text-white border-transparent">Masuk</Button>
                                    </Link>
                                )}
                            </nav>
                        </motion.div>
                    )
                }
            </AnimatePresence >
        </header >
    );
};

export default FamilyBusinessNavbar;
